import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { tripsApi } from '../../api/trips.api';
import { placesApi } from '../../api/places.api';
import { useVenueMap } from '../../hooks/useVenueMap';
import { useUnifiedRoutes } from '../../hooks/useUnifiedRoutes';
import { usePinFanOut } from '../../hooks/usePinFanOut';
import { useRoutePolylines } from '../../hooks/useRoutePolylines';
import { dedupeOriginPins, optionalStopIds, routeStopPins, tripPinNums } from '../../utils/routeGroups';
import { isPinComfortablyVisible } from '../../utils/mapViewport';
import { MAPS_API_KEY, LIBRARIES, MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM } from '../../utils/mapConstants';
import type { GeoPin } from '../../utils/pinCollision';
import type { CommunityTripDetail, ItineraryDay } from '../../types';
import RoutesPanel from '../map/RoutesPanel';
import VenueDetailCard, { type PinnedVenue } from '../map/VenueDetailCard';
import VenueCategoryFilterBar from '../map/VenueCategoryFilterBar';
import DayRouteFilterBar from '../map/DayRouteFilterBar';
import MapUnavailable from '../map/MapUnavailable';
import { VenuePin, SavedPlaceDot, RouteStopPin } from '../map/MapPins';
import Spinner from '../ui/Spinner';
import { Icon, MapPin, Route } from '../ui/Icon';

const EMPTY_STOPS: Set<string> = new Set();

/**
 * A community trip on the same map the trip itself uses — same pins, same
 * filters, same route panel — with every editing path left out: no search, no
 * card actions, no drag, no optimiser. The viewer isn't in the trip, and the
 * server would refuse the writes anyway (`requireTripAccessOrPublished`).
 *
 * The spend rules come with the hooks and are not re-decided here: routes open
 * on one day and every other day is cache-only (ADR-0011, `useUnifiedRoutes`);
 * photos and hours wait for the card's own button (ADR-0001, `useVenueMap`).
 * Whoever is looking pays, under their own cap.
 */
export default function CommunityTripMap({ trip }: { trip: CommunityTripDetail }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'venues' | 'routes'>('venues');
  // Local copy only because the routes hook wants a setter. Nothing here
  // writes to it: every action that would is absent from this page.
  const [days, setDays] = useState<ItineraryDay[]>(() => trip.days);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [showSavedPlaces, setShowSavedPlaces] = useState(false);
  const [showOptional, setShowOptional] = useState(true);
  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(
    trip.destinationLat != null && trip.destinationLng != null
      ? { lat: Number(trip.destinationLat), lng: Number(trip.destinationLng) }
      : null,
  );
  const [mapReady, setMapReady] = useState(false);
  const [fsContainer, setFsContainer] = useState<Element>(document.body);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapWrapRef = useRef<HTMLDivElement | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY ?? '',
    id: 'google-map-script',
    libraries: LIBRARIES,
  });

  useEffect(() => {
    const handler = () => setFsContainer(document.fullscreenElement ?? document.body);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Persisted coords are free; otherwise the server geocodes once for everyone.
  useEffect(() => {
    if (!isLoaded || center || !trip.destination) return;
    let cancelled = false;
    tripsApi.getCenter(trip.id)
      .then(c => { if (!cancelled && c?.lat != null && c?.lng != null) setCenter({ lat: c.lat, lng: c.lng }); })
      .catch(() => { /* keep the default centre */ });
    return () => { cancelled = true; };
  }, [isLoaded, center, trip.id, trip.destination]);

  const venue = useVenueMap({
    tripId: trip.id,
    tripDestination: trip.destination,
    days,
    mapRef, mapWrapRef,
    isLoaded,
    isMapReady: mapReady,
    fsContainer,
    center,
    rightInset: 0,
  });

  const routes = useUnifiedRoutes({
    trip, days, setDays,
    pinned: venue.pinned,
    isLoaded,
    active: mode === 'routes',
  });

  const unroutedVenues = useMemo(
    () => venue.pinned.filter(v => !venue.venueTodays.has(v.id)),
    [venue.pinned, venue.venueTodays],
  );
  const optionalStops = useMemo(() => optionalStopIds(routes.groups), [routes.groups]);
  const hiddenStops = showOptional ? EMPTY_STOPS : optionalStops;
  const hiddenPins = useMemo(
    () => new Set([...dedupeOriginPins(routes.groups), ...hiddenStops]),
    [routes.groups, hiddenStops],
  );
  // Several days count on from one to the next; a day alone starts at 1.
  const tripNums = useMemo(() => tripPinNums(routes.groups, hiddenPins), [routes.groups, hiddenPins]);

  const fanPoints = useMemo<GeoPin[]>(() => {
    if (mode === 'venues') return venue.filtered.map(v => ({ id: `venue-${v.id}`, lat: v.lat, lng: v.lng }));
    const points = routeStopPins(routes.groups, hiddenPins).map(p => ({ ...p, id: `stop-${p.id}` }));
    if (showSavedPlaces) points.push(...unroutedVenues.map(v => ({ id: `venue-${v.id}`, lat: v.lat, lng: v.lng })));
    return points;
  }, [mode, venue.filtered, routes.groups, hiddenPins, showSavedPlaces, unroutedVenues]);
  const pinFan = usePinFanOut(mapRef, fanPoints, isLoaded && mapReady);

  useRoutePolylines(mapRef, routes.groups, hiddenStops, isLoaded && mapReady && mode === 'routes');

  function openVenueCard(v: PinnedVenue) {
    venue.setActivePin(v.id);
    venue.setVenuePanel(v);
    venue.loadVenueDetails(v);
  }

  function closeCard() {
    venue.setActivePin(null);
    venue.setVenuePanel(null);
    setSelectedActivityId(null);
  }

  function selectStop(dayIdx: number, activityId: string) {
    const stop = routes.groups.find(g => g.dayIdx === dayIdx)?.stops.find(s => s.activity.id === activityId);
    if (!stop) return;
    setSelectedActivityId(activityId);
    if (stop.position) {
      const screen = venue.computePinScreen(stop.position.lat, stop.position.lng);
      const rect = mapWrapRef.current?.getBoundingClientRect();
      if (!screen || !rect || !isPinComfortablyVisible(screen, rect, { rightInset: 0 })) {
        mapRef.current?.panTo(stop.position);
      }
    }
    if (stop.venue) openVenueCard(stop.venue);
    else { venue.setActivePin(null); venue.setVenuePanel(null); }
  }

  if (!MAPS_API_KEY) {
    return <MapUnavailable reason="error" resetsAt={null} />;
  }

  const panelProps = {
    groups: routes.groups,
    destination: trip.destination,
    loading: routes.loading,
    error: routes.error,
    collapsedDays: routes.collapsedDays,
    busyDays: routes.busyDays,
    optimizingDay: null,
    legUnavailable: routes.legUnavailable,
    selectedActivityId,
    hiddenStops,
    onToggleCollapsed: routes.toggleCollapsed,
    onSelectStop: selectStop,
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5 gap-0.5 shrink-0">
          {(['venues', 'routes'] as const).map(m => (
            <button key={m} onClick={() => { setMode(m); closeCard(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                mode === m ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}>
              <Icon as={m === 'venues' ? MapPin : Route} size={14} />
              {m === 'venues' ? t('map.places') : t('map.routes')}
            </button>
          ))}
        </div>
        {venue.geocoding && <Spinner size="sm" />}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div ref={mapWrapRef} className="relative flex-1 h-[70dvh] md:h-[600px] rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800">
          {loadError ? (
            <MapUnavailable reason="error" resetsAt={null} />
          ) : !isLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
              <Spinner size="lg" />
            </div>
          ) : (
            <GoogleMap
              mapContainerClassName="w-full h-full"
              center={center ?? MAP_DEFAULT_CENTER}
              zoom={MAP_DEFAULT_ZOOM}
              onLoad={m => { mapRef.current = m; setMapReady(true); placesApi.reportMapLoad(trip.id); }}
              onClick={closeCard}
              options={{
                zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: true,
                styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
              }}
            >
              {mode === 'venues' && venue.filtered.map(v => (
                <VenuePin key={v.id} venue={v} fan={pinFan.get(`venue-${v.id}`)}
                  active={venue.activePin === v.id}
                  onClick={() => (venue.activePin === v.id ? closeCard() : openVenueCard(v))} />
              ))}
              {mode === 'routes' && showSavedPlaces && unroutedVenues.map(v => (
                <SavedPlaceDot key={`saved-${v.id}`} venue={v}
                  fan={pinFan.get(`venue-${v.id}`)} onClick={() => openVenueCard(v)} />
              ))}
              {mode === 'routes' && routes.groups.flatMap(group => group.stops.map(stop => (
                !stop.position || hiddenPins.has(stop.activity.id) ? null : (
                  <RouteStopPin key={`stop-${stop.activity.id}`}
                    stop={{ ...stop, position: stop.position }} color={group.color}
                    fan={pinFan.get(`stop-${stop.activity.id}`)}
                    num={tripNums?.get(stop.activity.id)}
                    active={selectedActivityId === stop.activity.id}
                    onClick={() => selectStop(group.dayIdx, stop.activity.id)} />
                )
              )))}
            </GoogleMap>
          )}

          {venue.venuePanel && venue.pinScreenPos && createPortal(
            <VenueDetailCard
              venue={venue.venuePanel}
              pinScreenPos={venue.pinScreenPos}
              placeDetail={venue.placeDetails[venue.venuePanel.id]}
              detailsLoading={venue.detailsLoading.has(venue.venuePanel.id)}
              onRevealDetails={() => venue.loadVenueDetails(venue.venuePanel!, { force: true })}
              cardDragOffset={venue.cardDragOffset}
              setCardDragOffset={venue.setCardDragOffset}
              venueTodays={venue.venueTodays}
              onClose={closeCard}
            />,
            fsContainer,
          )}
        </div>

        {mode === 'routes' && days.length > 0 && (
          <div className="md:w-80 max-h-[70dvh] md:max-h-[600px] flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <RoutesPanel {...panelProps} />
          </div>
        )}
      </div>

      {mode === 'venues' && venue.pinned.length > 0 && (
        <VenueCategoryFilterBar
          venues={venue.pinned}
          selectedCats={venue.selectedCats}
          onToggleCat={venue.toggleCat}
          onShowAll={venue.clearCats}
        />
      )}
      {mode === 'routes' && (
        <DayRouteFilterBar
          days={days}
          enabledDays={routes.enabledDays}
          onToggleDay={routes.toggleDay}
          onToggleAll={routes.toggleAllDays}
          daysWithRoute={routes.daysWithRoute}
          showOptional={showOptional}
          onToggleOptional={() => setShowOptional(o => !o)}
          optionalCount={optionalStops.size}
          showSavedPlaces={showSavedPlaces}
          onToggleSavedPlaces={() => setShowSavedPlaces(s => !s)}
          savedPlacesCount={unroutedVenues.length}
        />
      )}
    </div>
  );
}
